import { getApiHeaders } from '../utils/apiHeaders';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL as string;
const DRAFTS_PATH = `${API_BASE_URL}/api/package-orders/drafts`;

export interface CreateBookingDraftPayload {
  productType: 'package';
  packageId: string;
  customerId: number;
  queryId: number;
  passengers?: { adults: number; children?: number };
  startAt?: string | null;
  endAt?: string | null;
  providerRef?: Record<string, unknown>;
  fulfilmentPlan?: Record<string, unknown>;
  assignedId?: number;
  createdBy?: number;
}

function toLegacyDraft(order: any) {
  return {
    id: order.id,
    package_id: order.packageId,
    package_name: order.package?.title || order.packageSnapshot?.title || '',
    query_id: order.queryId,
    customer_id: order.customerId,
    product_type: 'package',
    status: order.orderStatus,
    provider_ref: order.selectionSnapshot?.quotation || {},
    booking_plan: order.fulfilmentPlan || {},
    total_amount_receivable_paise: order.totalAmountReceivablePaise ?? order.totalAmountPaise ?? 0,
    total_amount_payable_paise: order.totalAmountPayablePaise ?? 0,
    start_at: order.travelStartDate,
    end_at: order.travelEndDate,
    updated_at: order.updatedAt,
    created_at: order.createdAt,
  };
}

export const fetchBookingDrafts = async (queryId?: number, { includeMaterialized = false } = {}) => {
  if (!queryId) return [];
  const url = new URL(DRAFTS_PATH);
  url.searchParams.set('queryId', String(queryId));
  if (includeMaterialized) url.searchParams.set('includeMaterialized', 'true');
  const res = await fetch(url.toString(), { headers: getApiHeaders('none') });
  if (!res.ok) throw new Error(`Failed to fetch package-order drafts: ${res.status}`);
  const data = await res.json();
  return (Array.isArray(data?.items) ? data.items : []).map(toLegacyDraft);
};

export const extractLatestBookingDraft = async (queryId?: number) => {
  const drafts = await fetchBookingDrafts(queryId, { includeMaterialized: true });
  return drafts.slice().sort((a: any, b: any) => (
    new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime()
  ))[0] || null;
};

export async function createBookingDraft(payload: CreateBookingDraftPayload) {
  const res = await fetch(DRAFTS_PATH, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify({
      productType: 'package',
      packageId: payload.packageId,
      customerId: payload.customerId,
      queryId: payload.queryId,
      passengers: payload.passengers || { adults: 2, children: 0 },
      travelStartDate: payload.startAt,
      travelEndDate: payload.endAt,
      selectionSnapshot: { quotation: payload.providerRef || {} },
      fulfilmentPlan: payload.fulfilmentPlan || {},
    }),
  });
  if (!res.ok) throw new Error(`Failed to create package-order draft: ${await res.text()}`);
  return toLegacyDraft(await res.json());
}

export async function updateBookingDraft(draftId: number | string, payload: Partial<CreateBookingDraftPayload>) {
  const res = await fetch(`${DRAFTS_PATH}/${encodeURIComponent(String(draftId))}`, {
    method: 'PATCH',
    headers: getApiHeaders('json'),
    body: JSON.stringify({
      ...(payload.providerRef !== undefined ? { selectionSnapshot: { quotation: payload.providerRef } } : {}),
      ...(payload.fulfilmentPlan !== undefined ? { fulfilmentPlan: payload.fulfilmentPlan } : {}),
      ...(payload.startAt !== undefined ? { travelStartDate: payload.startAt } : {}),
      ...(payload.endAt !== undefined ? { travelEndDate: payload.endAt } : {}),
    }),
  });
  if (!res.ok) throw new Error(`Failed to update package-order draft: ${await res.text()}`);
  return toLegacyDraft(await res.json());
}

export async function materializeBookingDraft(
  draftId: number | string,
  financials?: { totalAmountReceivablePaise: number; totalAmountPayablePaise: number },
) {
  const res = await fetch(`${DRAFTS_PATH}/${encodeURIComponent(String(draftId))}/materialize`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    ...(financials ? { body: JSON.stringify(financials) } : {}),
  });
  if (!res.ok) throw new Error(`Failed to materialize package-order draft: ${await res.text()}`);
  return toLegacyDraft(await res.json());
}

/** Materialize the latest mutable package-order draft for the query, if one exists. */
export async function materializeBookingDraftForQuery(
  queryId?: number,
  financials?: { totalAmountReceivablePaise: number; totalAmountPayablePaise: number },
) {
  const drafts = await fetchBookingDrafts(queryId);
  const latest = drafts.slice().sort((a: any, b: any) => (
    new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime()
  ))[0];
  return latest ? materializeBookingDraft(latest.id, financials) : null;
}
